import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithTokenRefresh } from '../../common/baseQuery'

import { FormTemplate, FormTemplateMappingSource, FormTemplateMappingTarget, FormTemplatePreview } from '../../models/FormTemplate'

import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { FileInfo } from '../media/file'
import { paginate } from '../utils'

const _BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

interface CreateFormTemplateRequest extends BaseRequest {
  name: string
  scorecardId: string
  fileId: string
  targets: FormTemplateMappingTarget[]
  mapping: Record<string, FormTemplateMappingSource>
}

interface GetFormTemplatesRequest extends BasePageRequest {
  scorecardId?: string
}

interface GetAllFormTemplatesRequest extends BaseRequest {
  scorecardId?: string
}

interface GetFilledOutFormTemplateFileRequest extends BaseRequest {
  formTemplateId: string
  formResultId: string
}

interface GetFormTemplateByIdRequest extends BaseRequest {
  formTemplateId: string
}

export interface UpdateFormTemplatePayload {
  mapping: Record<string, FormTemplateMappingSource>
}

interface UpdateFormTemplateRequest extends BaseRequest {
  formTemplateId: string
  payload: UpdateFormTemplatePayload
}

interface DeleteFormTemplateRequest extends BaseRequest {
  formTemplateId: string
}

interface GetFormTemplatePreviewRequest extends BaseRequest {
  fileId: string
}

export const formTemplateApi = createApi({
  reducerPath: 'formTemplateApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['FormTemplate'],
  endpoints: (builder) => ({
    createFormTemplate: builder.mutation<FormTemplate, CreateFormTemplateRequest>({
      query: (request) => {
        const { accountId, name, scorecardId, fileId, targets, mapping } = request
        return {
          url: `/accounts/${accountId}/form-templates`,
          method: 'POST',
          body: {
            name,
            scorecardId,
            fileId,
            targets,
            mapping,
          },
        }
      },
      transformResponse: (response: BaseResponse<FormTemplate>) => {
        return response.data
      },
      invalidatesTags: ['FormTemplate'],
    }),
    getFormTemplates: builder.query<BasePageResponse<FormTemplate[]>, GetFormTemplatesRequest>({
      query: (request) => {
        const { accountId, pageRequest, scorecardId } = request
        return {
          url: `/accounts/${accountId}/form-templates`,
          params: {
            page: pageRequest.page,
            size: pageRequest.size,
            ...(scorecardId ? { scorecardId } : {}),
          },
        }
      },
      providesTags: ['FormTemplate'],
    }),
    getAllFormTemplates: builder.query<BaseResponse<FormTemplate[]>, GetAllFormTemplatesRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        const { accountId, scorecardId } = request
        return await paginate<FormTemplate>(
          {
            url: `/accounts/${accountId}/form-templates`,
            params: {
              ...(scorecardId ? { scorecardId } : {}),
            },
          },
          fetchWithBQ,
        )
      },
      providesTags: ['FormTemplate'],
    }),
    updateFormTemplate: builder.mutation<FormTemplate, UpdateFormTemplateRequest>({
      query: (request) => {
        const { accountId, formTemplateId, payload } = request
        return {
          url: `/accounts/${accountId}/form-templates/${formTemplateId}`,
          method: 'PATCH',
          body: payload,
        }
      },
      transformResponse: (response: BaseResponse<FormTemplate>) => {
        return response.data
      },
      invalidatesTags: ['FormTemplate'],
    }),
    getFormTemplateById: builder.query<FormTemplate, GetFormTemplateByIdRequest>({
      query: (request) => {
        const { accountId, formTemplateId } = request
        return {
          url: `/accounts/${accountId}/form-templates/${formTemplateId}`,
        }
      },
      transformResponse: (response: BaseResponse<FormTemplate>) => {
        return response.data
      },
      providesTags: ['FormTemplate'],
    }),
    deleteFormTemplateById: builder.mutation<void, DeleteFormTemplateRequest>({
      query: (request) => {
        const { accountId, formTemplateId } = request
        return {
          url: `/accounts/${accountId}/form-templates/${formTemplateId}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: ['FormTemplate'],
    }),
    getFormTemplatePreview: builder.mutation<FormTemplatePreview, GetFormTemplatePreviewRequest>({
      query: (request) => {
        const { accountId, fileId } = request
        return {
          url: `/accounts/${accountId}/form-templates/preview`,
          method: 'POST',
          body: {
            fileId,
          },
        }
      },
      transformResponse: (response: BaseResponse<FormTemplatePreview>) => {
        return response.data
      },
    }),
    getFilledOutFormTemplateFile: builder.mutation<FileInfo, GetFilledOutFormTemplateFileRequest>({
      query: (request) => {
        const { accountId, formTemplateId, formResultId } = request
        return {
          url: `/accounts/${accountId}/form-templates/${formTemplateId}/export`,
          method: 'POST',
          body: {
            formResultId,
          },
        }
      },
      transformResponse: (response: BaseResponse<FileInfo>) => {
        return response.data
      },
    }),
  }),
})

export const {
  useCreateFormTemplateMutation,
  useGetAllFormTemplatesQuery,
  useGetFormTemplateByIdQuery,
  useUpdateFormTemplateMutation,
  useDeleteFormTemplateByIdMutation,
  useGetFormTemplatePreviewMutation,
  useGetFilledOutFormTemplateFileMutation,
} = formTemplateApi
